/// <reference path="../../app.d.ts" />
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { first, debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { DigestService, PermissionService, TenantService, UserService, UtilService } from '../services';

@Component({
    selector: 'user-edit-profile',
    template: `
        <mat-card>
            <mat-card-content fxLayout="column" [formGroup]="form">
                <div>
                    <mat-form-field>
                        <input matInput placeholder="First Name" formControlName="firstName">
                        <mat-error *ngIf="form.get('firstName').hasError('required')">First Name is required.</mat-error>
                    </mat-form-field>
                    <mat-form-field fxFlexOffset="15px">
                        <input matInput placeholder="Last Name" formControlName="lastName">
                        <mat-error *ngIf="form.get('lastName').hasError('required')">Last Name is required.</mat-error>
                    </mat-form-field>
                    <mat-form-field fxFlexOffset="15px">
                        <input matInput placeholder="Suffix" formControlName="suffix">
                    </mat-form-field>
                </div>
                <div>
                    <mat-form-field fxFlex>
                        <input matInput placeholder="Email" formControlName="emailAddress">
                        <mat-error *ngIf="form.get('emailAddress').hasError('required')">Email Address is required.</mat-error>
                        <mat-error *ngIf="form.get('emailAddress')?.hasError('pattern')">Email address must be valid.</mat-error>
                        <mat-error *ngIf="form.get('emailAddress')?.hasError('validateUniqueEmail')">Email address must be unique.</mat-error>
                    </mat-form-field>
                    <mat-form-field fxFlex fxFlexOffset="15px">
                        <mat-select placeholder="Timezone" formControlName="timeZone">
                            <mat-option *ngFor="let zone of timezones" [value]="zone.value">{{ zone.viewValue }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>
                <div fxFlex>
                    <mat-form-field fxFlex>
                        <mat-select placeholder="Status" formControlName="status">
                            <mat-option *ngIf="user && user.status === 'INVITED'" value="INVITED">invited</mat-option>
                            <mat-option value="ACTIVE">active</mat-option>
                            <mat-option value="INACTIVE">inactive</mat-option>
                            <mat-option value="TRIAL_EXPIRED">trial expired</mat-option>
                        </mat-select>
                    </mat-form-field>
                    <div fxFlex="70px" fxFlexOffset="15px" style="padding-top: 15px;">
                        <mat-checkbox formControlName="isAdministrator">Admin</mat-checkbox>
                    </div>
                    <div *ngIf="allowTopicCreators" fxFlex="110px" fxFlexOffset="15px" style="padding-top: 15px;">
                        <mat-checkbox formControlName="isTopicCreator">Topic Creator</mat-checkbox>
                    </div>
                </div>
                <div fxFlex style="padding-bottom: 45px;">
                    <mat-form-field fxFlex>
                        <input matInput autocomplete="new-password" placeholder="Password" type="password" formControlName="password">
                        <mat-error *ngIf="form.get('password')?.hasError('pattern')">Password must contain at least one upper case letter, one lowercase letter, one number and be at least 8 characters long.</mat-error>
                        <mat-error *ngIf="!form.get('password')?.hasError('pattern') && form.get('password')?.hasError('noMatch')">Passwords must match.</mat-error>
                    </mat-form-field>
                    <mat-form-field fxFlex fxFlexOffset="15px">
                        <input matInput placeholder="Confirm Password" type="password" formControlName="confirmPassword">
                        <mat-error *ngIf="form.get('confirmPassword')?.hasError('noMatch')">Passwords must match.</mat-error>
                    </mat-form-field>
                </div>
            </mat-card-content>
            <mat-card-actions>
                <button mat-raised-button color="primary" [disabled]="form.invalid" (click)="save()">Save</button>
            </mat-card-actions>
        </mat-card>
    `
})
export class UserEditProfileComponent implements OnChanges, OnDestroy {
    private cleanupTime = new Subject<void>();
    form: FormGroup;
    currentUser: any;
    @Input() userId: string;
    user: Attensa.User;
    timezones;
    hostname: string;
    digestCreateEnabled: boolean;
    allowTopicCreators: boolean;

    
    constructor(public snackBar: MatSnackBar,
                private fb: FormBuilder,
                private digestService: DigestService,
                private permissionService: PermissionService,
                private tenantService: TenantService,
                private utilService: UtilService,
                private userService: UserService){}

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.form) {
            this.form = this.fb.group({
                firstName: ['', Validators.required],
                lastName: ['', Validators.required],
                suffix: [],
                password: ['', [Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$'), this.passwordsMatch.bind(this)]],
                confirmPassword: ['', this.passwordsMatch.bind(this)],
                emailAddress: ['', 
                            [Validators.required, Validators.pattern('[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$')],
                            this.uniqueEmail.bind(this)],
                timeZone: [''],
                status: [''], 
                isAdministrator: [],
                isTopicCreator: []
            });
            this.currentUser = this.userService.getCurrentUser();
            this.timezones = this.utilService.displayTimezones;
            this.tenantService.getCurrentTenant().pipe(first()).subscribe(tenant => {
            this.digestCreateEnabled = tenant.settings.noInitialDigest === 'true' ? undefined : true;
            this.hostname = tenant.hostName;
            this.allowTopicCreators = _.includes(tenant.settings.allowRoleTypes, 'STREAM_CREATOR');
        });    
        }

        if (changes.userId.currentValue) {
            this.userService.fetchUser(this.userId).subscribe((user: Attensa.User) => {
                this.user = user;
                this.form.patchValue(user);
                this.form.get('isAdministrator').setValue(this.permissionService.userHasTenantAction(user, 'MANAGE_TENANT'));
                if (user.id === this.currentUser.id) {
                    this.form.get('isAdministrator').disable();
                    this.form.get('isTopicCreator').disable();
                    this.form.get('status').disable();
                }
                this.form.get('isTopicCreator').setValue(this.permissionService.userHasTenantAction(user, 'CREATE_STREAM'));
                ['firstName', 'lastName'].forEach(key => this.form.get(key).markAsTouched());
            });
        }
    }

    passwordsMatch(c: AbstractControl): { noMatch: boolean } {
        if (this.form) {
            if (this.form.get('password').value !== this.form.get('confirmPassword').value && 
                (this.form.get('password').value.length || this.form.get('confirmPassword').value.length)) {
                return { noMatch: true};
            } else {
                if (this.form.get('password') === c && this.form.get('confirmPassword').hasError('noMatch')) {
                    setTimeout(() => this.form.get('confirmPassword').updateValueAndValidity());
                } else if(this.form.get('confirmPassword') === c && this.form.get('password').hasError('noMatch')) {
                    setTimeout(() => this.form.get('password').updateValueAndValidity());
                }
            }
        }
    }

    uniqueEmail(control: AbstractControl): Observable<{ [key: string]: any }> {
        return this.userService.getUserByEmail(control.value, this.hostname)
        .pipe(
            debounceTime(250),
            distinctUntilChanged(),
            map(user => user.id === this.user.id ? undefined : { validateUniqueEmail: true })
        );
    }

    save() {
        const formVal = this.form.value;
        const attrs = ['firstName', 'lastName', 'suffix', 'password', 'confirmPassword', 'emailAddress', 'timeZone', 'status'];
        attrs.forEach(attr => this.user[attr] = formVal[attr]);
        this.userService.updateUser(this.user)
        .pipe(
            tap(() => {
                if (formVal.isAdministrator && !this.permissionService.userHasTenantAction(this.user, 'MANAGE_TENANT')) {
                    this.userService.addTenantRole(this.user.id, this.user.tenantId, 'TENANT_ADMIN').pipe(first()).subscribe();
                } else if (!formVal.isAdministrator && this.permissionService.userHasTenantAction(this.user, 'MANAGE_TENANT')) {
                    this.userService.removeTenantRole(this.user.id, this.user.tenantId, 'TENANT_ADMIN').pipe(first()).subscribe();
                } else {
                    if (formVal.isTopicCreator && !this.permissionService.userHasTenantAction(this.user, 'CREATE_STREAM')) {
                        this.userService.addTenantRole(this.user.id, this.user.tenantId, 'STREAM_CREATOR').pipe(first()).subscribe();
                    } else if (!formVal.isTopicCreator && this.permissionService.userHasTenantAction(this.user, 'CREATE_STREAM')) {
                        this.userService.removeTenantRole(this.user.id, this.user.tenantId, 'STREAM_CREATOR').pipe(first()).subscribe();
                    }
                }
            }),
            tap(() => {
                if (this.user.status = 'ACTIVE') {
                    this.digestService.getDigestConfigurationsForUser(this.user.id, this.digestCreateEnabled).subscribe();
                }
            }),
            first()
        ).subscribe(() => {
            this.snackBar.open(`${this.user.firstName} ${this.user.lastName} updated.`, 'OK', { duration: 3000});
        });
    }

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}